import {chain, isEmpty} from 'lodash';
import React, {FC, PropsWithChildren, useCallback, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {useInitialEffect} from '../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {intersperseNodes} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {SearchTagModel} from '../../../models/api/searchTagModel';
import {TagModel} from '../../../models/api/tagModel';
import {getCompany} from '../../../store/data/company/companySelectors';
import {getTagsTable, getUserStarredTagId} from '../../../store/data/tags/tagsSelectors';
import {renderTagIcon} from '../../common/dropdowns/dropdownItemTagIcon';
import {FilterRowGenerator} from '../../common/filters/resourceMultiSelectFilterDropdown';
import {computeFilterDropdownSourceAsync} from '../../common/filters/sourceFilter/filterDropdownSource';
import {ResourceMultiSelectInput} from '../../common/resourceMultiSelectInput/resourceMultiSelectInput';
import {
  buildFetcher,
  buildRowData,
  fetchMissingTags,
  mapIdsToUndeletedTags,
  StyledTagIcon,
  StyledTagTitle,
  StyledTagWrapper,
  TagsMap,
} from './tagFilterHelpers';
import {LensFormMultiselectProps} from './types';

/*
 * Props.
 */

interface LensFormMultiselectInboxesProps extends Omit<LensFormMultiselectProps<TagModel>, 'choices'> {
  namespace: string | undefined;
  inboxIds: ReadonlyArray<number>;
  idsToDisable: ReadonlyArray<number>;
  noTags: boolean;
}

/*
 * Style.
 */

const StyledValueWrapper = styled.div`
  display: flex;
  gap: 4px;
  ${ellipsis()}
`;

/*
 * Component.
 */

export const LensFormMultiSelectTags: FC<PropsWithChildren<LensFormMultiselectInboxesProps>> = (props) => {
  const {selectedIds, onSetItemIds, namespace, inboxIds, idsToDisable, noTags} = props;

  const company = useAppSelector(getCompany);
  const [initialSelectedTags, setInitialSelectedTags] = useState<ReadonlyArray<SearchTagModel>>([]);
  // Tags that are available for using to render the dropdown button value.
  //  These are tags that have been selected at some point and may or may not be currently selected.
  //  We only store these to guarantee we can show the name of selected tags as the button value.
  const [tagsMap, setTagsMap] = useState<TagsMap>({});
  const {byId: tags, deletedById} = useAppSelector(getTagsTable);
  const userStarredTagId = useAppSelector(getUserStarredTagId);
  const dispatch = useAppDispatch();

  useInitialEffect(() => {
    fetchMissingTags(selectedIds, deletedById, company, tags, dispatch, setTagsMap);
  });

  useEffect(() => {
    setInitialSelectedTags(mapIdsToUndeletedTags(selectedIds, deletedById, tagsMap, tags));
  }, [tags, tagsMap, selectedIds, deletedById]);

  const dropdownSource = useMemo(() => {
    const fetcher = buildFetcher(company, namespace, initialSelectedTags, inboxIds);
    return computeFilterDropdownSourceAsync(fetcher);
  }, [company, inboxIds, initialSelectedTags, namespace]);

  const buildRowGenerator: FilterRowGenerator<SearchTagModel> = useCallback(
    (searchTag, dropdownSelectedIds, makeOnItemSelect, isSearching, initialSelectedIds) =>
      buildRowData(
        searchTag,
        dropdownSelectedIds,
        makeOnItemSelect,
        isSearching,
        initialSelectedIds,
        setTagsMap,
        tagsMap,
        dispatch,
        idsToDisable,
      ),
    [dispatch, idsToDisable, tagsMap],
  );

  return (
    <ResourceMultiSelectInput
      dropdownSource={dropdownSource}
      selectedItemIds={selectedIds}
      computeFilterGenerator={buildRowGenerator}
      onSetItems={onSetItemIds}
      onClearAll={() => onSetItemIds([])}
      renderValue={() => renderValue(props, tagsMap, userStarredTagId)}
      renderPlaceholder={() => <Trans id="lens-form-multiselect-tags-placeholder">Select shared tags</Trans>}
      label={
        props.label || <Trans id="lens-form-multiselect-tags-label">Filtered by any of these tags</Trans>
      }
      dropdownTitle={<Trans id="lens-form-multiselect-tags-dropdown-title">Shared tags</Trans>}
      isDisabled={isEmpty(inboxIds) || noTags}
      isRequired={false}
      messageForDisabled={getMessageForDisabled(noTags, isEmpty(inboxIds))}
      hasError={props.hasError}
      errorMessage={props.errorMessage}
    />
  );
};

/*
 * Helpers.
 */

function getMessageForDisabled(noTags: boolean, noInboxIds: boolean) {
  if (noTags) {
    return <Trans id="lens-form-multiselect-excluding-all-tags-message">Currently excluding all tags.</Trans>;
  }
  if (noInboxIds) {
    return (
      <Trans id="lens-form-multiselect-missing-inbox-message">
        Field will be enabled once an inbox is selected.
      </Trans>
    );
  }
  return undefined;
}

function renderValue(props: LensFormMultiselectInboxesProps, tagsMap: TagsMap, userStarredTagId: number) {
  const {selectedIds} = props;

  if (!selectedIds.length) {
    return null;
  }

  const selectedTags = chain(selectedIds)
    .map((id) => tagsMap[id])
    .compact()
    .value();

  return (
    <StyledValueWrapper>
      {intersperseNodes(
        selectedTags.map((t) => ({
          node: (
            <StyledTagWrapper key={`tag-${t.id}`}>
              <StyledTagIcon>
                {renderTagIcon(t, {isStarredTag: t.id === userStarredTagId, isSmall: true})}
              </StyledTagIcon>
              <StyledTagTitle>{t.name}</StyledTagTitle>
            </StyledTagWrapper>
          ),
          key: String(t.id),
        })),
        () => ', ',
      )}
    </StyledValueWrapper>
  );
}
